import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, AlertCircle, ArrowLeft, Mail, Eye, EyeOff, CheckCircle } from 'lucide-react'
import axios from 'axios'
import type { ApiResponse } from '@/types'

// Brand colors
const BRAND_NAVY = '#1e3a5f'
const BRAND_PINK = '#ff4081'

// Small logo for the form panel - bigger size
const SmallLogo = () => (
  <div className="flex items-center justify-center p-4 rounded-xl border border-gray-200 bg-white shadow-sm w-20 h-20">
    <svg viewBox="0 0 60 60" className="h-12 w-12" fill="none">
      <path d="M30 8 L52 20 L30 32 L8 20 Z" fill="#1e3a5f" />
      <path d="M30 16 L52 28 L30 40 L8 28 Z" fill="#7cb9e8" />
      <path d="M30 24 L52 36 L30 48 L8 36 Z" fill="#ff4081" />
      <path d="M30 32 L52 44 L30 56 L8 44 Z" fill="#4a90c2" />
    </svg>
  </div>
)

// Google Icon
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

interface LoginInitResponse {
  requires_otp: boolean
  session_token: string
  user_email: string
  expires_at: string
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { verifyOtp, isLoading: authLoading } = useAuth()

  // Login form state
  const [userLoginId, setUserLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // OTP state
  const [showOtpForm, setShowOtpForm] = useState(false)
  const [sessionToken, setSessionToken] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isResending, setIsResending] = useState(false)

  // Get the redirect path from location state, or default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  // Check for URL params (errors and success messages)
  useEffect(() => {
    const errorParam = searchParams.get('error')
    const signupParam = searchParams.get('signup')

    if (signupParam === 'success') {
      setSuccessMessage('Account created successfully! Please sign in with your credentials.')
    }

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        oauth_not_configured: 'Google Sign-In is not configured.',
        invalid_state: 'Invalid authentication state. Please try again.',
        token_exchange_failed: 'Failed to authenticate with Google. Please try again.',
        failed_to_get_user_info: 'Failed to get user information from Google.',
        login_failed: 'Login failed. User may not be registered.',
        account_inactive: 'Your account is inactive. Please contact support.',
        no_tenant_access: 'You do not have access to any organization.',
        tenant_inactive: 'Your organization is inactive. Please contact support.',
      }
      setError(errorMessages[errorParam] || 'An error occurred during sign-in.')
    }
  }, [searchParams])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isResending) return

    setIsResending(true)
    setError(null)

    try {
      const response = await axios.post<ApiResponse<LoginInitResponse>>('/api/auth/resend-otp', {
        session_token: sessionToken,
      })

      const data = response.data.data || response.data
      const resendData = data as LoginInitResponse

      // Update session token (backend generates a new one)
      setSessionToken(resendData.session_token)
      setUserEmail(resendData.user_email)
      setOtpCode(['', '', '', '', '', ''])
      setResendCooldown(60) // 60 second cooldown
      // Focus first OTP input
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'Failed to resend code. Please try again.')
      } else {
        setError('Failed to resend code. Please try again.')
      }
    } finally {
      setIsResending(false)
    }
  }

  // Handle login form submission - initiates OTP flow
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await axios.post<ApiResponse<LoginInitResponse>>('/api/auth/login', {
        user_login_id: userLoginId,
        password: password,
      })

      const data = response.data.data || response.data
      const loginData = data as LoginInitResponse

      if (loginData.requires_otp) {
        setSessionToken(loginData.session_token)
        setUserEmail(loginData.user_email)
        setShowOtpForm(true)
        setOtpCode(['', '', '', '', '', ''])
        // Focus first OTP input after render
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        // Backend returns error message in 'message' field, fallback to 'error' for legacy support
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'Login failed. Please try again.')
      } else {
        setError('Login failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Only allow digits

    const newOtp = [...otpCode]
    newOtp[index] = value.slice(-1) // Only take last character
    setOtpCode(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  // Handle OTP paste
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      setOtpCode(pastedData.split(''))
      otpInputRefs.current[5]?.focus()
    }
  }

  // Handle OTP backspace
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  // Handle OTP verification
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    const code = otpCode.join('')
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code')
      setIsLoading(false)
      return
    }

    try {
      await verifyOtp(sessionToken, code)
      navigate(from, { replace: true })
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        // Backend returns error message in 'message' field, fallback to 'error' for legacy support
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'OTP verification failed. Please try again.')
      } else {
        setError('OTP verification failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Go back to login form
  const handleBackToLogin = () => {
    setShowOtpForm(false)
    setSessionToken('')
    setUserEmail('')
    setOtpCode(['', '', '', '', '', ''])
    setError(null)
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // OTP Verification Form
  if (showOtpForm) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-12 w-full h-full">
            {/* Main tagline */}
            <div>
              <p className="text-sm font-semibold tracking-widest mb-4" style={{ color: BRAND_PINK }}>
                BLUEPRINT DESK
              </p>
              <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight">
                The <em className="italic" style={{ color: '#94a3b8' }}>operating<br />system</em> for<br />
                <span className="font-semibold">high-growth<br />agencies.</span>
              </h1>
              <div className="w-16 h-1 mt-8" style={{ backgroundColor: BRAND_PINK }} />
            </div>
          </div>
        </div>

        {/* Right Panel - OTP Form */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            {/* Small logo */}
            <div className="flex justify-center mb-12">
              <SmallLogo />
            </div>

            {/* OTP Form Card */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(30, 58, 95, 0.1)' }}>
                  <Mail className="h-8 w-8" style={{ color: BRAND_NAVY }} />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Enter Verification Code</h1>
              <p className="text-gray-500 mt-2">
                We've sent a 6-digit code to {userEmail}
              </p>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-center gap-2">
                {otpCode.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => { otpInputRefs.current[index] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    className="w-12 h-12 text-center text-xl font-semibold bg-white border border-gray-300 rounded-lg focus:border-gray-400 focus:ring-0"
                    disabled={isLoading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-white rounded-lg font-medium hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <div className="text-center text-sm text-gray-500">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isResending}
                  className="font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  style={{ color: BRAND_PINK }}
                >
                  {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-lg"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // Login Form
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 w-full h-full">
          {/* Main tagline */}
          <div>
            <p className="text-sm font-semibold tracking-widest mb-4" style={{ color: BRAND_PINK }}>
              BLUEPRINT DESK
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight">
              The <em className="italic" style={{ color: '#94a3b8' }}>operating<br />system</em> for<br />
              <span className="font-semibold">high-growth<br />agencies.</span>
            </h1>
            <div className="w-16 h-1 mt-8" style={{ backgroundColor: BRAND_PINK }} />
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Small logo */}
          <div className="flex justify-center mb-12">
            <SmallLogo />
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-500">Secure access to your Blueprint Desk dashboard.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {successMessage && (
              <Alert className="border-green-500 bg-green-50 text-green-800">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Work Email field */}
            <div>
              <Input
                id="userLoginId"
                type="text"
                placeholder="Work Email"
                value={userLoginId}
                onChange={(e) => setUserLoginId(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="username"
                className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
              />
            </div>

            {/* Password field */}
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="current-password"
                className="h-12 bg-white border border-gray-300 rounded-lg px-4 pr-20 focus:ring-0 focus:border-gray-400"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700 uppercase tracking-wide"
                >
                  Forgot?
                </Link>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
              />
              <label
                htmlFor="remember"
                className="text-sm text-gray-600 leading-none cursor-pointer"
              >
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Sign In button */}
            <Button
              type="submit"
              className="w-full h-12 text-white rounded-lg font-medium hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign Into Dashboard'
              )}
            </Button>

            {/* SSO Divider - always show */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-4 text-gray-400 tracking-widest font-medium">
                  Secure SSO
                </span>
              </div>
            </div>

            {/* Google Sign In - always show the button, backend will handle if not configured */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-lg border-gray-300 hover:bg-gray-50"
              onClick={() => {
                window.location.href = '/api/auth/google'
              }}
              disabled={isLoading}
            >
              <GoogleIcon />
              Continue with Google
            </Button>
          </form>

          {/* Sign up link */}
          <div className="mt-8 text-center">
            <span className="text-gray-500">New to Blueprint?</span>{' '}
            <Link
              to="/signup"
              className="font-medium"
              style={{ color: BRAND_PINK }}
            >
              Start 14-day trial
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

